import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../AuthContext';
import { apiJson } from '../apiClient';
import { normalizeRole } from '../utils/userLocale';
import { fileToImageDataUrl } from '../utils/imageUpload';
import { PLATFORM_BRAND } from '../utils/tenantBranding';
import './BusinessSettingsPage.css';

const BusinessSettingsPage = ({ onNavigate }) => {
  const { user, refreshMe } = useAuth();
  const isOwner = normalizeRole(user?.role) === 'owner';
  const logoInputRef = useRef(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [businessTypeLabel, setBusinessTypeLabel] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiJson('/owner/tenant-settings');
      const t = data.tenant || {};
      setName(t.name || '');
      setPhone(t.phone || '');
      setAddress(t.address || '');
      setBusinessTypeLabel(t.businessTypeLabel || t.businessType || '');
      setLogoUrl(t.logoUrl || null);
      setLogoPreview(t.logoUrl || null);
      setRemoveLogo(false);
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Could not load settings.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOwner) void load();
    else {
      setName(user?.tenant?.name || '');
      setPhone(user?.tenant?.phone || '');
      setAddress(user?.tenant?.address || '');
      setBusinessTypeLabel(user?.tenant?.businessTypeLabel || '');
      setLogoPreview(user?.tenant?.logoUrl || null);
      setLoading(false);
    }
  }, [isOwner, load, user]);

  const handleLogoPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setMsg(null);
    try {
      const dataUrl = await fileToImageDataUrl(file);
      setLogoPreview(dataUrl);
      setRemoveLogo(false);
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Invalid image.' });
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setRemoveLogo(true);
    setMsg(null);
  };

  const handleUseDefaultLogo = () => {
    setLogoPreview(null);
    setRemoveLogo(true);
  };

  if (!isOwner) {
    return (
      <div className="page-bleed business-settings-page">
        <h1>Business profile</h1>
        <div className="business-settings-card">
          {logoPreview ? (
            <div className="business-settings-brand-preview">
              <img src={logoPreview} alt="" className="business-settings-brand-logo" />
            </div>
          ) : null}
          <div className="business-settings-row">
            <span>Business name</span>
            <strong>{user?.tenant?.name || '—'}</strong>
          </div>
          <div className="business-settings-row">
            <span>Type</span>
            <strong>{user?.tenant?.businessTypeLabel || '—'}</strong>
          </div>
          <p className="business-settings-note">Only the owner can edit business details and branding.</p>
        </div>
      </div>
    );
  }

  const handleSave = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const body = {
        name: name.trim(),
        phone: phone.trim() || null,
        address: address.trim() || null,
      };
      if (removeLogo) {
        body.removeLogo = true;
      } else if (logoPreview && logoPreview !== logoUrl) {
        body.logo = logoPreview;
      }
      await apiJson('/owner/tenant-settings', {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      await refreshMe();
      await load();
      setMsg({
        type: 'success',
        text: 'Saved. Your logo and business name appear on PDF reports, invoices, and downloads.',
      });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Save failed.' });
    } finally {
      setBusy(false);
    }
  };

  const showingDefault = !logoPreview;

  return (
    <div className="page-bleed business-settings-page">
      <h1 className="business-settings-page__title">Business profile &amp; branding</h1>
      <p className="business-settings-page__lead">
        Your business name and logo on receipts, PDF reports, purchase orders, and invoices. Without a custom logo, we
        use the default <strong>{PLATFORM_BRAND.platformName}</strong> mark.
      </p>

      {msg ? <div className={`business-settings-msg business-settings-msg--${msg.type}`}>{msg.text}</div> : null}

      {loading ? (
        <p>Loading…</p>
      ) : (
        <form className="business-settings-card business-settings-form" onSubmit={handleSave}>
          <section className="business-settings-brand">
            <h2 className="business-settings-brand__title">Company logo</h2>
            <p className="business-settings-brand__hint">
              Square image works best (PNG or JPG, max 2.5 MB). Shown on every PDF you download.
            </p>
            <div className="business-settings-brand-row">
              <div className="business-settings-brand-preview">
                {logoPreview ? (
                  <img src={logoPreview} alt="" className="business-settings-brand-logo" />
                ) : (
                  <img
                    src={PLATFORM_BRAND.defaultLogoPath}
                    alt=""
                    className="business-settings-brand-logo business-settings-brand-logo--default"
                  />
                )}
              </div>
              <div className="business-settings-brand-actions">
                <button
                  type="button"
                  className="business-settings-brand-btn"
                  disabled={busy}
                  onClick={() => logoInputRef.current?.click()}
                >
                  Upload logo
                </button>
                <button
                  type="button"
                  className="business-settings-brand-btn business-settings-brand-btn--ghost"
                  disabled={busy || showingDefault}
                  onClick={handleRemoveLogo}
                >
                  Remove custom logo
                </button>
                <button
                  type="button"
                  className="business-settings-brand-btn business-settings-brand-btn--ghost"
                  disabled={busy}
                  onClick={handleUseDefaultLogo}
                >
                  Use {PLATFORM_BRAND.platformName} default
                </button>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="business-settings-brand-input"
                  onChange={handleLogoPick}
                />
              </div>
            </div>
          </section>

          <div className="business-settings-row business-settings-row--readonly">
            <span>Business type</span>
            <strong>{businessTypeLabel || '—'}</strong>
          </div>
          <label>
            Business name
            <input required value={name} onChange={(e) => setName(e.target.value)} disabled={busy} />
          </label>
          <label>
            Phone
            <input value={phone} onChange={(e) => setPhone(e.target.value)} disabled={busy} placeholder="+255…" />
          </label>
          <label>
            Address
            <textarea
              rows={2}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              disabled={busy}
              placeholder="Office or main plant address"
            />
          </label>
          {user?.branch?.name ? (
            <div className="business-settings-row business-settings-row--readonly">
              <span>Your branch</span>
              <strong>{user.branch.name}</strong>
            </div>
          ) : null}
          <button type="submit" className="business-settings-save" disabled={busy}>
            Save profile &amp; branding
          </button>
        </form>
      )}

      <button type="button" className="business-settings-back" onClick={() => onNavigate('settings')}>
        ← Back to settings
      </button>
    </div>
  );
};

export default BusinessSettingsPage;
